/**
 * Client-side aggregation for the Assets Overview page.
 *
 * ⚠️ THREE INDEPENDENT CONCEPTS — do NOT conflate (asset-domain-v2-design.md §2.3):
 *   - isNew(a)          = origin === 'ivt_incubated'  → green top bar + [NEW] badge
 *   - isThisMonth(iso)  = createdAt in current month  → "本月新增" KPI (+N), plain text
 *   - maturity          →  card background color
 * `isNew` (origin) and `isThisMonth` (createdAt) are SEPARATE predicates. The green
 * marker follows isNew ONLY; the +N KPI follows isThisMonth ONLY.
 */
import {
  AssetOrigin,
  GrowType,
  RetainReview,
  type AssetMaturity,
  type AssetSummary,
  type StorylineSummary,
} from '../../../client/IvtHubClient';

/**
 * A "new" asset = incubated origin. Drives the green top bar + [NEW] badge. NOT createdAt.
 *
 * ⚠️ CASE-INSENSITIVE compare: the smithy contract models origin as lowercase enum
 * values (`ivt_incubated`), but the deployed beta IVTHub currently serializes the
 * enum NAME (uppercase `IVT_INCUBATED`) — a backend serialization/contract mismatch
 * (tracked separately; backend should emit the lowercase enum value). We normalize
 * here so New-Assets filtering works regardless of which case the server sends.
 */
export function isNew(asset: AssetSummary): boolean {
  return (asset.origin ?? '').toLowerCase() === AssetOrigin.IVT_INCUBATED;
}

/** createdAt falls in the same calendar month+year as `now` (default: real now).
 *  Drives the "本月新增" KPI only — unrelated to origin / isNew. TZ-safe: parses the
 *  ISO instant and compares UTC year+month (createdAt is stored as a UTC instant). */
export function isThisMonth(createdAt: string | undefined, now: Date = new Date()): boolean {
  if (!createdAt) return false;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return false;
  return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth();
}

/** 4 maturity buckets in display order (探索→建设→打法→规模化). */
export const MATURITY_ORDER: AssetMaturity[] = ['EXPLORING', 'BUILDING', 'PLAYBOOK', 'SCALING'];

export interface MaturityBucket {
  maturity: AssetMaturity;
  count: number;
  /** How many of this bucket were created this month (the "(+N)" figure). */
  newThisMonth: number;
}

/** Count assets per maturity + this-month delta, in MATURITY_ORDER. */
export function byMaturity(assets: AssetSummary[], now: Date = new Date()): MaturityBucket[] {
  return MATURITY_ORDER.map((maturity) => {
    const inBucket = assets.filter((a) => a.maturity === maturity);
    return {
      maturity,
      count: inBucket.length,
      newThisMonth: inBucket.filter((a) => isThisMonth(a.createdAt, now)).length,
    };
  });
}

export interface IndustryColumn {
  industry: string;
  assets: AssetSummary[];
}

export interface TopicRow {
  topic: string;
  assets: AssetSummary[];
}

/**
 * Group assets into TOPIC rows for the "View by Topic" swimlane (horizontal: one
 * row per topic, its assets laid out across). Grouped by AssetSummary.topic (the
 * top-level topic name, single-valued) — one asset appears in exactly one row.
 * Assets with NO topic (industry-anchored) are excluded from View by Topic
 * (mirror of byIndustry excluding topic-anchored ones). Rows alphabetical by topic;
 * assets within a row by display name.
 */
export function byTopic(assets: AssetSummary[]): TopicRow[] {
  const map = new Map<string, AssetSummary[]>();
  for (const a of assets) {
    if (!a.topic) continue; // industry-anchored → not shown in View by Topic
    const list = map.get(a.topic);
    if (list) list.push(a);
    else map.set(a.topic, [a]);
  }
  return [...map.keys()]
    .sort((x, y) => x.localeCompare(y, 'en'))
    .map((topic) => ({
      topic,
      assets: [...map.get(topic)!].sort((a, b) =>
        (a.name ?? a.useCase ?? '').localeCompare(b.name ?? b.useCase ?? '', 'en'),
      ),
    }));
}

/**
 * Group assets into industry columns for the "View by Industry" swimlane.
 *
 * Columns are driven by the INDUSTRY UNIVERSE (`industryOrder`, from ListIndustries),
 * NOT by which industries happen to have assets — so every industry shows a column,
 * including empty ones (e.g. ECOM with 0 assets → an empty column). Any industry
 * seen on an asset but missing from `industryOrder` is appended (defensive, so a
 * new/unknown industry still surfaces). Assets with NO industry (topic-anchored)
 * are EXCLUDED here — they belong to "View by Topic" (see byTopic, the mirror).
 * Display names come straight from ListIndustries / the asset rows (no code↔name map).
 */
export function byIndustry(assets: AssetSummary[], industryOrder: string[] = []): IndustryColumn[] {
  const map = new Map<string, AssetSummary[]>();
  for (const a of assets) {
    if (!a.industry) continue; // topic-anchored → not shown in View by Industry
    const list = map.get(a.industry);
    if (list) list.push(a);
    else map.set(a.industry, [a]);
  }
  // Column set = industry universe (alphabetical) ∪ any extra industries seen on
  // assets but absent from the universe (appended, alphabetical).
  const universe = [...industryOrder].sort((x, y) => x.localeCompare(y, 'en'));
  const extras = [...map.keys()]
    .filter((i) => !industryOrder.includes(i))
    .sort((x, y) => x.localeCompare(y, 'en'));
  const cols = [...universe, ...extras];
  // De-dup (universe could contain repeats) while preserving order.
  const seen = new Set<string>();
  return cols
    .filter((i) => (seen.has(i) ? false : (seen.add(i), true)))
    .map((industry) => ({ industry, assets: map.get(industry) ?? [] }));
}

// ── Structured nav tree (Asset Detail left tree, "Structured" mode) ──────────
// Industry → Sales Area → Use Case → Asset (from AssetSummary.solutionMapLinks),
// plus Topic → Sub-topic → Asset (from topicLinks), plus an "未分类" bucket for
// assets with neither link. An asset with N solutionMapLinks appears under EACH
// of its (industry, salesArea, useCase) leaves (per product: repeat is fine).

/** A leaf holding the assets that sit directly under it. */
export interface StructuredLeaf {
  key: string; // useCase name (industry branch) or subTopicName (topic branch)
  assets: AssetSummary[];
  /** UC primary key (solution_map.id) for an industry-branch Use Case leaf — the
   *  stable, same-name-safe key the UC detail route uses. From the UcAssetLink that
   *  placed this leaf. Undefined for topic sub-topic leaves (no UC PK). */
  solutionMapId?: number;
}
/** A mid level: solution area (industry branch) or sub-topic parent grouping. */
export interface StructuredNode {
  key: string; // salesArea name / topicName
  leaves: StructuredLeaf[];
}
/** A top branch: one industry (with sales areas) or the Topic root. */
export interface StructuredBranch {
  key: string; // industry name / topicName
  nodes: StructuredNode[];
}

export interface StructuredTree {
  /** Industry → Sales Area → Use Case → Asset. */
  industries: StructuredBranch[];
  /** Topic → Sub-topic → Asset. */
  topics: StructuredBranch[];
  /** Assets with no solutionMapLink and no topicLink (flat leaf under "未分类"). */
  unclassified: AssetSummary[];
}

const cmp = (x: string, y: string) => x.localeCompare(y, 'en');

/** Sort assets by display name (name ?? useCase). Shared by Structured + Menu trees. */
const sortAssets = (list: AssetSummary[]): AssetSummary[] =>
  [...list].sort((a, b) => cmp(a.name ?? a.useCase ?? '', b.name ?? b.useCase ?? ''));

/** The full classification skeleton used to pre-seed EMPTY branches/leaves so the
 *  tree lists every UC / sub-topic even when no asset is linked to it. All optional
 *  — omit for the old asset-only shape (used by pure-aggregation unit tests). */
export interface TreeUniverse {
  ucEntries?: { industry: string; solutionArea: string; useCase: string; solutionMapId: number }[];
  industries?: string[];
  subTopics?: { topicName: string; subTopicName: string }[];
  topicNames?: string[];
}

/**
 * Build the Structured tree. Uses AssetSummary.solutionMapLinks (each carries
 * industry / solutionArea / useCase) and topicLinks (topicName / subTopicName).
 * Deterministic: every level sorted alphabetically; assets within a leaf sorted
 * by display name. Missing pieces are tolerated (a link lacking solutionArea/
 * useCase, or a topicLink lacking names, is skipped rather than crashing).
 *
 * When `universe` is given, every UC (industry/area/useCase) and every sub-topic in
 * it is pre-seeded as an EMPTY leaf, so classifications with no asset still show
 * (with a (0) count). Assets then fill their leaves. Empty industries/topics from
 * the universe also appear as branches with no leaves.
 */
export function buildStructuredTree(assets: AssetSummary[], universe?: TreeUniverse): StructuredTree {
  // industry -> salesArea -> useCase -> AssetSummary[]
  const ind = new Map<string, Map<string, Map<string, AssetSummary[]>>>();
  // topicName -> subTopicName -> AssetSummary[]
  const top = new Map<string, Map<string, AssetSummary[]>>();
  const unclassified: AssetSummary[] = [];
  // (industry/salesArea/useCase) leaf key -> its UC solution_map.id (the UC detail
  // route key). All assets under one leaf share the same UC, so the first non-null
  // link's id wins; recorded as the leaf is filled.
  const leafSolutionMapId = new Map<string, number>();

  const put3 = (a: AssetSummary, i: string, s: string, u: string, solutionMapId?: number) => {
    let sa = ind.get(i);
    if (!sa) ind.set(i, (sa = new Map()));
    let uc = sa.get(s);
    if (!uc) sa.set(s, (uc = new Map()));
    const list = uc.get(u);
    if (list) list.push(a);
    else uc.set(u, [a]);
    const leafKey = JSON.stringify([i, s, u]);
    if (solutionMapId != null && !leafSolutionMapId.has(leafKey)) leafSolutionMapId.set(leafKey, solutionMapId);
  };
  const put2 = (a: AssetSummary, t: string, st: string) => {
    let sub = top.get(t);
    if (!sub) top.set(t, (sub = new Map()));
    const list = sub.get(st);
    if (list) list.push(a);
    else sub.set(st, [a]);
  };
  // Ensure an (industry, salesArea, useCase) leaf exists with NO asset (empty leaf).
  const seedUc = (i: string, s: string, u: string, solutionMapId?: number) => {
    let sa = ind.get(i);
    if (!sa) ind.set(i, (sa = new Map()));
    let uc = sa.get(s);
    if (!uc) sa.set(s, (uc = new Map()));
    if (!uc.has(u)) uc.set(u, []);
    const leafKey = JSON.stringify([i, s, u]);
    if (solutionMapId != null && !leafSolutionMapId.has(leafKey)) leafSolutionMapId.set(leafKey, solutionMapId);
  };

  // Pre-seed the full classification skeleton so empty UCs / sub-topics still show.
  // Done BEFORE the asset loop; assets then fill these leaves (or create new ones
  // for links not present in the universe — defensive, e.g. a just-created UC).
  if (universe) {
    for (const e of universe.ucEntries ?? []) {
      if (e.industry && e.solutionArea && e.useCase) seedUc(e.industry, e.solutionArea, e.useCase, e.solutionMapId);
    }
    // Empty industries (no UC at all) → a branch with no leaves.
    for (const i of universe.industries ?? []) if (i && !ind.has(i)) ind.set(i, new Map());
    for (const s of universe.subTopics ?? []) {
      if (!s.topicName || !s.subTopicName) continue;
      let sub = top.get(s.topicName);
      if (!sub) top.set(s.topicName, (sub = new Map()));
      if (!sub.has(s.subTopicName)) sub.set(s.subTopicName, []);
    }
    // Empty topics (no sub-topic) → a branch with no leaves.
    for (const t of universe.topicNames ?? []) if (t && !top.has(t)) top.set(t, new Map());
  }

  for (const a of assets) {
    const ucLinks = (a.solutionMapLinks ?? []).filter((l) => l.industry && l.solutionArea && l.useCase);
    const tLinks = (a.topicLinks ?? []).filter((l) => l.topicName && l.subTopicName);
    if (ucLinks.length > 0) {
      for (const l of ucLinks)
        put3(a, l.industry as string, l.solutionArea as string, l.useCase as string, l.solutionMapId ?? undefined);
    }
    if (tLinks.length > 0) {
      for (const l of tLinks) put2(a, l.topicName as string, l.subTopicName as string);
    }
    if (ucLinks.length === 0 && tLinks.length === 0) unclassified.push(a);
  }

  const industries: StructuredBranch[] = [...ind.keys()].sort(cmp).map((i) => ({
    key: i,
    nodes: [...ind.get(i)!.keys()].sort(cmp).map((s) => ({
      key: s,
      leaves: [...ind.get(i)!.get(s)!.keys()].sort(cmp).map((u) => ({
        key: u,
        assets: sortAssets(ind.get(i)!.get(s)!.get(u)!),
        solutionMapId: leafSolutionMapId.get(JSON.stringify([i, s, u])),
      })),
    })),
  }));

  const topics: StructuredBranch[] = [...top.keys()].sort(cmp).map((t) => ({
    key: t,
    nodes: [
      {
        key: t, // topic branch has a single implicit node; sub-topics are the leaves
        leaves: [...top.get(t)!.keys()].sort(cmp).map((st) => ({
          key: st,
          assets: sortAssets(top.get(t)!.get(st)!),
        })),
      },
    ],
  }));

  return { industries, topics, unclassified: sortAssets(unclassified) };
}

// ── Menu nav tree ("View by Storyline") ──────────────────────────────────────
// Industry → Storyline → Asset, plus a per-industry "Unassigned" bucket for
// assets in that industry with no storyline link. Storylines come from
// ListStorylines (so EMPTY storylines — 0 linked assets — still show as nodes,
// which asset-derived reverse-lookup can't produce). An asset links to a
// storyline via AssetSummary.storylineLinks[].storylineId. A single asset can
// sit under multiple storylines (N:M); it appears under each. Storyline is a
// per-industry concept, so grouping is keyed by the storyline's industry, and
// an asset's Unassigned membership is judged within its own industry.

/** One storyline leaf: the storyline + the assets linked to it. */
export interface MenuStoryline {
  storylineId: string;
  name: string;
  assets: AssetSummary[];
}
/** One industry branch in the Menu tree. */
export interface MenuBranch {
  industry: string;
  storylines: MenuStoryline[];
  /** Assets in this industry with NO storyline link (the "Unassigned (N)" node). */
  unassigned: AssetSummary[];
}

/**
 * Build the Menu tree. Every storyline from `storylines` becomes a leaf under its
 * industry (empty ones show with a (0) count); assets fill leaves by
 * storylineLinks. Each industry also gets an Unassigned bucket of its assets that
 * carry no storyline link. Industries are the union of ListIndustries `industries`
 * + any industry that appears on a storyline (so a storyline in an industry not in
 * the list still shows). Deterministic: industries + storylines sorted by name,
 * assets within a leaf by display name.
 */
export function buildMenuTree(
  assets: AssetSummary[],
  storylines: StorylineSummary[],
  industries: string[] = [],
): MenuBranch[] {
  // industry -> storylineId -> assets
  const byIndustryStoryline = new Map<string, Map<string, AssetSummary[]>>();
  // industry -> unassigned assets
  const unassignedByIndustry = new Map<string, AssetSummary[]>();
  // storylineId -> {name, industry}, from the ListStorylines skeleton.
  const storylineMeta = new Map<string, { name: string; industry: string }>();

  // Seed every storyline as an empty leaf under its industry (so 0-asset
  // storylines still render).
  for (const s of storylines) {
    if (!s.id || !s.industry) continue;
    storylineMeta.set(s.id, { name: s.name ?? s.id, industry: s.industry });
    let sm = byIndustryStoryline.get(s.industry);
    if (!sm) byIndustryStoryline.set(s.industry, (sm = new Map()));
    if (!sm.has(s.id)) sm.set(s.id, []);
  }

  // Place each asset. An asset with storyline links goes under each linked
  // storyline (matched by storylineId); one with none goes to its industry's
  // Unassigned bucket. A link whose storyline isn't in the skeleton is skipped
  // (stale link — e.g. storyline deleted; defensive).
  for (const a of assets) {
    const links = (a.storylineLinks ?? []).filter((l) => l.storylineId && storylineMeta.has(l.storylineId));
    if (links.length === 0) {
      // Unassigned is per the asset's OWN industry (skip industry-less assets —
      // storyline is a per-industry concept; a Topic-anchored null-industry asset
      // has no place in the Menu tree).
      if (a.industry) {
        let u = unassignedByIndustry.get(a.industry);
        if (!u) unassignedByIndustry.set(a.industry, (u = []));
        u.push(a);
      }
      continue;
    }
    for (const l of links) {
      const meta = storylineMeta.get(l.storylineId!)!;
      byIndustryStoryline.get(meta.industry)!.get(l.storylineId!)!.push(a);
    }
  }

  // Industry set = ListIndustries ∪ industries that own a storyline ∪ industries
  // that have unassigned assets. De-dup preserving a stable sorted order.
  const industrySet = new Set<string>();
  for (const i of industries) if (i) industrySet.add(i);
  for (const i of byIndustryStoryline.keys()) industrySet.add(i);
  for (const i of unassignedByIndustry.keys()) industrySet.add(i);

  return [...industrySet]
    .sort(cmp)
    .map((industry) => {
      const sm = byIndustryStoryline.get(industry);
      const storylineLeaves: MenuStoryline[] = sm
        ? [...sm.entries()]
            .map(([storylineId, leafAssets]) => ({
              storylineId,
              name: storylineMeta.get(storylineId)!.name,
              assets: sortAssets(leafAssets),
            }))
            .sort((x, y) => cmp(x.name, y.name))
        : [];
      return {
        industry,
        storylines: storylineLeaves,
        unassigned: sortAssets(unassignedByIndustry.get(industry) ?? []),
      };
    });
}

// ── GROW Menu tree (DAT-9159): Industry → GROW type → Asset ─────────────────────
// From AssetSummary.growTypes / retainReview / growMetrics (ListAssets carries them
// for exactly this). Topic-anchored assets (no industry) have no place here.

/** GROW type order — the tree's groups and the detail page's type editor. */
export const GROW_ORDER: GrowType[] = [GrowType.GAIN, GrowType.RETAIN, GrowType.BOOST, GrowType.WIDEN];

/** One industry branch: a group per GROW type (always all four, possibly empty) and
 *  the assets with no type set (the "未设置" node). */
export interface GrowBranch {
  industry: string;
  groups: { type: GrowType; assets: AssetSummary[] }[];
  unset: AssetSummary[];
}

const RETAIN_RANK: Record<string, number> = { [RetainReview.PASSED]: 0, [RetainReview.FAILED]: 1 };

/** The value a type's group sorts by, best first: higher metric first; Retain Passed →
 *  Failed → 未评审. `null` = cannot be computed (「—」), which always sorts last. */
function growSortKey(type: GrowType, a: AssetSummary): number | null {
  const m = a.growMetrics;
  switch (type) {
    case GrowType.GAIN:
      return m?.gainCoverage ?? null;
    case GrowType.BOOST:
      return m?.boostDealCount ?? null;
    case GrowType.WIDEN:
      return m?.widenLaunchedArr ?? null;
    case GrowType.RETAIN:
      // Negated rank so "higher first" puts Passed on top; 未评审 is not a value.
      return a.retainReview != null && a.retainReview in RETAIN_RANK ? -RETAIN_RANK[a.retainReview] : null;
    default:
      return null;
  }
}

/** Sort one type's group: by its metric (best first, 「—」 last), ties by display name. */
export function sortGrowGroup(type: GrowType, list: AssetSummary[]): AssetSummary[] {
  const name = (a: AssetSummary) => a.name ?? a.useCase ?? '';
  return [...list].sort((a, b) => {
    const ka = growSortKey(type, a);
    const kb = growSortKey(type, b);
    if (ka !== kb) {
      if (ka == null) return 1;
      if (kb == null) return -1;
      return kb - ka;
    }
    return cmp(name(a), name(b));
  });
}

/**
 * Build the GROW Menu tree. Every industry in `industries` is seeded (empty ones show
 * with a (0) count), plus any industry an asset carries. An asset goes under each GROW
 * type it has — the backend stores a set, though the editor is single-select today — or
 * under 未设置 when it has none. Industries sorted by name.
 */
export function buildGrowTree(assets: AssetSummary[], industries: string[] = []): GrowBranch[] {
  const byIndustry = new Map<string, AssetSummary[]>();
  for (const i of industries) if (i && !byIndustry.has(i)) byIndustry.set(i, []);
  for (const a of assets) {
    if (!a.industry) continue;
    let list = byIndustry.get(a.industry);
    if (!list) byIndustry.set(a.industry, (list = []));
    list.push(a);
  }
  return [...byIndustry.keys()].sort(cmp).map((industry) => {
    const list = byIndustry.get(industry)!;
    return {
      industry,
      groups: GROW_ORDER.map((type) => ({
        type,
        assets: sortGrowGroup(
          type,
          list.filter((a) => a.growTypes?.includes(type)),
        ),
      })),
      unset: sortAssets(list.filter((a) => !a.growTypes || a.growTypes.length === 0)),
    };
  });
}

export interface AssetKpis {
  total: number;
  /** origin === ivt_incubated */
  newCount: number;
  /** created this month (createdAt), unrelated to newCount */
  createdThisMonth: number;
}

export function computeKpis(assets: AssetSummary[], now: Date = new Date()): AssetKpis {
  return {
    total: assets.length,
    newCount: assets.filter(isNew).length,
    createdThisMonth: assets.filter((a) => isThisMonth(a.createdAt, now)).length,
  };
}
